import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Badge,
  Button,
  Divider,
  Dropdown,
  EmptyState,
  ErrorAlert,
  Heading,
  Icon,
  ListRow,
  Text,
  Time,
  useDropdownClose,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import type { ProgrammePlatformClient } from "@/platform";
import type { NotificationFeedItem } from "@/server/types";
import { begin } from "@/utils/begin";

interface NotificationCentreProps {
  client: ProgrammePlatformClient;
  onOpen: (notification: NotificationFeedItem) => void;
}

/** The 56px top bar plus a gap, so the panel opens clear of its border. */
const TOPBAR_CLEARANCE = 64;
const REFRESH_INTERVAL_MS = 60_000;
const NOTIFICATION_PAGE_SIZE = 20;
const MAX_NOTIFICATION_HISTORY = 100;

function notificationIcon(eventType: NotificationFeedItem["eventType"]): string {
  if (eventType.startsWith("MODULE_")) {
    return eventType === "MODULE_APPROVED" ? "check-circle" : "file-text";
  }
  if (eventType === "PROGRAMME_DEVELOPMENT_STARTED") {
    return "rocket-launch";
  }
  return "users-three";
}

interface NotificationPanelProps {
  items: NotificationFeedItem[];
  unreadCount: number;
  loading: boolean;
  error: boolean;
  canLoadOlder: boolean;
  onRetry: () => void;
  onOpen: (notification: NotificationFeedItem, close: () => void) => Promise<void>;
  onMarkAllRead: () => Promise<void>;
  onLoadOlder: () => void;
}

/** The feed inside the dropdown, which is what can close it. */
function NotificationPanel({
  items,
  unreadCount,
  loading,
  error,
  canLoadOlder,
  onRetry,
  onOpen,
  onMarkAllRead,
  onLoadOlder,
}: NotificationPanelProps): React.ReactElement {
  const close = useDropdownClose();
  let listContent: React.ReactNode;
  if (loading) {
    listContent = (
      <div className="px-4 py-2">
        <ListBones rows={3} label="Loading notifications" />
      </div>
    );
  } else if (error) {
    listContent = (
      <ErrorAlert className="m-3">
        Notifications could not be loaded.
        <Button size="xs" variant="link" className="ml-1 p-0" onClick={onRetry}>
          Try again
        </Button>
      </ErrorAlert>
    );
  } else if (items.length === 0) {
    listContent = <EmptyState title="No notifications yet" density="row" className="m-3" />;
  } else {
    listContent = (
      <ul className="divide-y divide-base-300">
        {items.map((notification) => (
          <li key={notification.id}>
            <ListRow
              framed={false}
              tone="none"
              className={notification.readAt ? "bg-base-100" : "bg-accent-soft"}
              leading={
                <span
                  className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full ${
                    notification.readAt
                      ? "bg-base-200 text-muted"
                      : "bg-accent-soft text-primary-strong"
                  }`}
                >
                  <Icon name={notificationIcon(notification.eventType)} aria-hidden />
                </span>
              }
              title={notification.title}
              description={<span className="break-words">{notification.message}</span>}
              onClick={() => onOpen(notification, () => close())}
              data-testid={`notification-item-${notification.id}`}
            >
              <Text size="sm" tone="muted" className="mt-1">
                <Time value={notification.createdAt} variant="relative" />
              </Text>
            </ListRow>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <Heading level={2} size="md">
            Notifications
          </Heading>
          {unreadCount > 0 || items.length > 0 ? (
            <Text size="sm" tone="muted">
              {unreadCount > 0 ? `${unreadCount} unread` : "You are up to date"}
            </Text>
          ) : null}
        </div>
        {unreadCount > 0 ? (
          <Button
            size="xs"
            variant="ghost"
            icon="checks"
            onClick={() => onMarkAllRead()}
            data-testid="notification-mark-all-read"
          >
            Mark all read
          </Button>
        ) : null}
      </div>
      <Divider />
      <div
        className="max-h-96 overflow-x-hidden overflow-y-auto"
        aria-live="polite"
        data-testid="notification-list-viewport"
      >
        {listContent}
      </div>
      {!loading && !error && canLoadOlder ? (
        <>
          <Divider />
          <div className="p-2">
            <Button
              size="sm"
              variant="ghost"
              icon="clock-counter-clockwise"
              className="w-full"
              onClick={onLoadOlder}
              data-testid="notification-load-older"
            >
              Show older notifications
            </Button>
          </div>
        </>
      ) : null}
    </>
  );
}

/** Signed-in user's notification feed and read-state controls. */
export function NotificationCentre({ client, onOpen }: NotificationCentreProps) {
  const [items, setItems] = useState<NotificationFeedItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [historyLimit, setHistoryLimit] = useState(NOTIFICATION_PAGE_SIZE);

  // A promise chain rather than async/await, so every state update lands in a
  // continuation. The effect below calls this on mount, and an update in the
  // synchronous part of that call would cascade a second render before the
  // first has painted.
  // Bumped by every refresh and every read mark; a feed answer only lands if
  // nothing has started since it was asked for.
  const generation = useRef(0);
  const refresh = useCallback((): Promise<void> => {
    generation.current += 1;
    const mine = generation.current;
    return begin(() => client.getNotificationFeed({ limit: historyLimit })).then(
      (feed) => {
        if (mine !== generation.current) {
          return;
        }
        setItems(feed.items);
        setUnreadCount(feed.unreadCount);
        setError(false);
        setLoading(false);
      },
      () => {
        if (mine !== generation.current) {
          return;
        }
        setError(true);
        setLoading(false);
      },
    );
  }, [client, historyLimit]);

  useEffect(() => {
    refresh();
    const interval = window.setInterval(() => refresh(), REFRESH_INTERVAL_MS);
    const handleFocus = () => refresh();
    window.addEventListener("focus", handleFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refresh]);

  const markRead = useCallback(
    async (notification: NotificationFeedItem, close: () => void): Promise<void> => {
      if (!notification.readAt) {
        const readAt = new Date().toISOString();
        generation.current += 1;
        const marked = generation.current;
        setItems((current) =>
          current.map((item) => (item.id === notification.id ? { ...item, readAt } : item)),
        );
        setUnreadCount((current) => Math.max(0, current - 1));
        try {
          await client.markNotificationRead(notification.id);
          // A refresh asked for while the mark was in flight may predate it,
          // so read again now that the mark has landed.
          if (generation.current !== marked) {
            refresh();
          }
        } catch {
          // Put the unread mark back, but still go where the reader asked.
          refresh();
        }
      }
      close();
      onOpen(notification);
    },
    [client, onOpen, refresh],
  );

  const markAllRead = useCallback(async (): Promise<void> => {
    const readAt = new Date().toISOString();
    generation.current += 1;
    const marked = generation.current;
    setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt ?? readAt })));
    setUnreadCount(0);
    try {
      await client.markAllNotificationsRead();
      if (generation.current !== marked) {
        await refresh();
      }
    } catch {
      await refresh();
    }
  }, [client, refresh]);

  return (
    <Dropdown
      aria-label={
        unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications, none unread"
      }
      triggerAppearance="ghost"
      trigger={
        <span className="relative inline-flex">
          <Icon name="bell" size="lg" aria-hidden />
          {unreadCount > 0 ? (
            <Badge
              size="xs"
              tone="primary"
              className="absolute -right-3 -top-2.5 min-w-4 px-1"
              data-testid="notification-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          ) : null}
        </span>
      }
      panelWidth="wide"
      minTop={TOPBAR_CLEARANCE}
      panel={
        <NotificationPanel
          items={items}
          unreadCount={unreadCount}
          loading={loading}
          error={error}
          canLoadOlder={items.length >= historyLimit && historyLimit < MAX_NOTIFICATION_HISTORY}
          onRetry={refresh}
          onOpen={markRead}
          onMarkAllRead={markAllRead}
          onLoadOlder={() =>
            setHistoryLimit((current) =>
              Math.min(current + NOTIFICATION_PAGE_SIZE, MAX_NOTIFICATION_HISTORY),
            )
          }
        />
      }
      data-testid="notification-centre"
      triggerTestId="notification-toggle"
    />
  );
}
